import { api, uploadZip, Prototype, VersionInfo, CreateVersionResponse, getApiUrl } from "./api";
import { currentEmail } from "./auth";

const VIEWER_BASE = "https://p.ai.rewind.engineering";
let filterMode: "all" | "mine" | string = "all"; // "all", "mine", or an email address
let allPrototypes: Prototype[] = [];

export function renderWho() {
  const el = document.getElementById("who");
  if (!el) return;

  const span = document.createElement("span");
  span.className = "who-email";
  span.textContent = `Signed in as ${currentEmail() ?? ""}`;

  const link = document.createElement("a");
  link.href = "#";
  link.className = "signout-link";
  link.textContent = "Sign out";
  link.addEventListener("click", (e) => {
    e.preventDefault();
    sessionStorage.clear();
    window.location.reload();
  });

  el.replaceChildren(span, link);
}

export async function renderApp(root: HTMLElement) {
  root.innerHTML = `
    <section class="intro">
      <p>Upload and share Claude-generated HTML prototypes with your team.</p>
      <p>Upload a <strong>.zip file</strong> containing everything needed for your prototype (HTML, CSS, JS, images).
        Include an <code>index.html</code> at the root, or a single <code>.html</code> file that will be used as the entry point.</p>
      <p class="intro-note">Prototype viewer links are <strong>publicly accessible</strong> to anyone with the URL — no sign-in required to view.</p>
    </section>
    <section class="card">
      <h2>Upload</h2>
      <div class="upload-fields">
        <input type="text" id="title" placeholder="Title" />
        <input type="text" id="description" placeholder="Brief description (optional)" maxlength="200" />
        <div class="upload-row">
          <input type="file" id="file" accept=".zip,application/zip" hidden />
          <button id="chooseFileBtn" class="btn-secondary" type="button">Choose .zip file</button>
          <span id="fileName" class="file-name">No file chosen</span>
          <button id="uploadBtn" class="btn-primary">Upload</button>
        </div>
      </div>
      <div id="uploadStatus"></div>
    </section>
    <section class="card">
      <div class="list-header">
        <h2>Prototypes</h2>
        <div class="list-controls">
          <div class="filter-toggle">
            <button id="filterAll" class="filter-btn active">All</button>
            <button id="filterMine" class="filter-btn">Mine</button>
          </div>
          <select id="userFilter" class="user-filter" style="display:none">
            <option value="">All users</option>
          </select>
          <button id="refreshBtn" class="refresh-btn" title="Refresh list">&#8635;</button>
        </div>
      </div>
      <div id="list"><p>Loading…</p></div>
    </section>
  `;
  document.getElementById("uploadBtn")!.addEventListener("click", onUpload);
  document.getElementById("chooseFileBtn")!.addEventListener("click", () => {
    document.getElementById("file")!.click();
  });
  document.getElementById("file")!.addEventListener("change", () => {
    const file = (document.getElementById("file") as HTMLInputElement).files?.[0];
    document.getElementById("fileName")!.textContent = file ? file.name : "No file chosen";
  });
  document.getElementById("filterAll")!.addEventListener("click", () => setFilter("all"));
  document.getElementById("filterMine")!.addEventListener("click", () => setFilter("mine"));
  document.getElementById("userFilter")!.addEventListener("change", onUserFilterChange);
  document.getElementById("refreshBtn")!.addEventListener("click", () => refreshList());
  await refreshList();
}

function setFilter(mode: "all" | "mine") {
  filterMode = mode;
  document.getElementById("filterAll")!.classList.toggle("active", mode === "all");
  document.getElementById("filterMine")!.classList.toggle("active", mode === "mine");
  const userSelect = document.getElementById("userFilter") as HTMLSelectElement;
  if (mode === "all") {
    userSelect.style.display = "";
    userSelect.value = "";
  } else {
    userSelect.style.display = "none";
  }
  renderFilteredList();
}

function onUserFilterChange() {
  const userSelect = document.getElementById("userFilter") as HTMLSelectElement;
  filterMode = userSelect.value || "all";
  renderFilteredList();
}

function populateUserFilter(prototypes: Prototype[]) {
  const select = document.getElementById("userFilter") as HTMLSelectElement;
  const owners = [...new Set(prototypes.map((p) => p.owner))].sort();
  const current = select.value;
  select.innerHTML = `<option value="">All users</option>`;
  for (const owner of owners) {
    const opt = document.createElement("option");
    opt.value = owner;
    opt.textContent = owner;
    select.appendChild(opt);
  }
  select.value = current;
  select.style.display = filterMode === "mine" ? "none" : "";
}

function getFilteredPrototypes(): Prototype[] {
  if (filterMode === "mine") {
    return allPrototypes.filter((p) => p.owner === currentEmail());
  }
  if (filterMode !== "all" && filterMode !== "") {
    return allPrototypes.filter((p) => p.owner === filterMode);
  }
  return allPrototypes;
}

async function onUpload() {
  const title = (document.getElementById("title") as HTMLInputElement).value.trim();
  const description = (document.getElementById("description") as HTMLInputElement).value.trim();
  const file = (document.getElementById("file") as HTMLInputElement).files?.[0];
  const status = document.getElementById("uploadStatus")!;
  if (!title) { status.textContent = "Title required"; return; }
  if (!file) { status.textContent = "Pick a .zip file"; return; }

  status.textContent = "Creating…";
  try {
    const presigned = await api.create(title, description || undefined);
    status.textContent = "Uploading…";
    await uploadZip(file, presigned);
    status.textContent = "Extracting…";
    const final = await pollUntilReady(presigned.slug);
    if (final.status === "ready") {
      const url = `${VIEWER_BASE}/${presigned.slug}/`;
      status.innerHTML = `Ready: <a href="${url}" target="_blank">${url}</a>`;
    } else {
      status.innerHTML = `<span class="error">${escape(final.error ?? "Failed")}</span>`;
    }
    (document.getElementById("title") as HTMLInputElement).value = "";
    (document.getElementById("description") as HTMLInputElement).value = "";
    (document.getElementById("file") as HTMLInputElement).value = "";
    await refreshList();
  } catch (e) {
    status.innerHTML = `<span class="error">${escape((e as Error).message)}</span>`;
  }
}

async function pollUntilReady(slug: string, version = 1): Promise<VersionInfo> {
  for (let i = 0; i < 60; i++) {
    const v = await api.getVersion(slug, version);
    if (v.status !== "pending") return v;
    await new Promise((r) => setTimeout(r, 2000));
  }
  throw new Error("Timed out waiting for extraction");
}

async function refreshList() {
  const el = document.getElementById("list")!;
  try {
    const { prototypes } = await api.list();
    allPrototypes = prototypes;
    populateUserFilter(prototypes);
    renderFilteredList();
  } catch (e) {
    el.innerHTML = `<div class="error">${escape((e as Error).message)}</div>`;
  }
}

function renderFilteredList() {
  const el = document.getElementById("list")!;
  const filtered = getFilteredPrototypes();
  if (filtered.length === 0) {
    const msg = filterMode === "mine"
      ? "You haven't uploaded any prototypes yet."
      : allPrototypes.length === 0
        ? "No prototypes yet."
        : "No prototypes match this filter.";
    el.innerHTML = `<p>${msg}</p>`;
    return;
  }
  el.innerHTML = filtered.map(renderCard).join("");
  for (const p of filtered) {
    document.getElementById(`del-${p.slug}`)?.addEventListener("click", () => onDelete(p.slug));
    document.getElementById(`keep-${p.slug}`)?.addEventListener("click", () => onKeep(p.slug));
    document.getElementById(`unkeep-${p.slug}`)?.addEventListener("click", () => onUnkeep(p.slug));
    // Version toggle
    document.getElementById(`versions-toggle-${p.slug}`)?.addEventListener("click", () => onToggleVersions(p));
    // Upload new version (owner only)
    if (p.owner === currentEmail()) {
      document.getElementById(`version-choose-${p.slug}`)?.addEventListener("click", () => {
        document.getElementById(`version-file-${p.slug}`)!.click();
      });
      document.getElementById(`version-file-${p.slug}`)?.addEventListener("change", () => {
        const file = (document.getElementById(`version-file-${p.slug}`) as HTMLInputElement).files?.[0];
        document.getElementById(`version-filename-${p.slug}`)!.textContent = file ? file.name : "No file chosen";
      });
      document.getElementById(`version-upload-btn-${p.slug}`)?.addEventListener("click", () => onUploadVersion(p.slug));
      document.getElementById(`gen-token-${p.slug}`)?.addEventListener("click", () => onGenerateToken(p.slug));
    }
  }
  for (const btn of el.querySelectorAll<HTMLButtonElement>(".copy-btn")) {
    btn.addEventListener("click", () => {
      navigator.clipboard.writeText(btn.dataset.url ?? "");
      btn.textContent = "\u2713";
      setTimeout(() => { btn.textContent = "\uD83D\uDCCB"; }, 1500);
    });
  }
}

function expiryLabel(p: Prototype): string {
  if (p.kept) return `<span class="expiry-kept">Kept permanently</span>`;
  if (!p.expiresAt) return "";
  const daysLeft = Math.ceil((p.expiresAt * 1000 - Date.now()) / 86400000);
  if (daysLeft <= 0) return `<span class="expiry-soon">Expiring soon</span>`;
  if (daysLeft <= 7) return `<span class="expiry-soon">Expires in ${daysLeft}d</span>`;
  return `<span class="expiry-normal">Expires in ${daysLeft}d</span>`;
}

function renderCard(p: Prototype): string {
  const isMine = p.owner === currentEmail();
  const url = `${VIEWER_BASE}/${p.slug}/`;
  const desc = p.description ? `<div class="proto-desc">${escape(p.description)}</div>` : "";
  const expiry = expiryLabel(p);
  const keepBtn = isMine
    ? (!p.kept
        ? `<button id="keep-${p.slug}" class="keep-btn" title="Keep this prototype permanently">Keep</button>`
        : `<button id="unkeep-${p.slug}" class="unkeep-btn" title="Restore 90-day expiry">Expire</button>`)
    : "";
  return `
    <div class="prototype">
      <div class="proto-info">
        <div class="proto-title"><a href="${url}" target="_blank">${escape(p.title)}</a> <span class="version-badge">v${p.currentVersion}</span></div>
        ${desc}
        <div class="proto-url">
          <a href="${url}" target="_blank">${escape(url)}</a>
          <button class="copy-btn" data-url="${escape(url)}" title="Copy URL">&#128203;</button>
        </div>
        <div class="proto-meta">
          <span>Uploaded by: <strong>${escape(p.owner)}</strong></span>
          <span>Uploaded on: <strong>${escape(new Date(p.uploadedAt).toLocaleString())}</strong></span>
          ${expiry ? `<span>${expiry}</span>` : ""}
        </div>
      </div>
      <div class="proto-actions">
        ${keepBtn}
        ${isMine ? `<button id="del-${p.slug}" class="del-btn">Delete</button>` : ""}
      </div>
      <div class="proto-expandable">
        <button class="versions-toggle" id="versions-toggle-${p.slug}" data-slug="${p.slug}">&#9654; ${p.versionCount} version${p.versionCount === 1 ? "" : "s"}</button>
        <div class="versions-list" id="versions-${p.slug}" style="display:none"></div>
        ${isMine ? `
          <div class="version-upload" id="version-upload-${p.slug}" style="display:none">
            <input type="file" id="version-file-${p.slug}" accept=".zip,application/zip" hidden />
            <button class="btn-secondary btn-sm" id="version-choose-${p.slug}">Choose .zip</button>
            <span class="file-name" id="version-filename-${p.slug}">No file chosen</span>
            <input type="text" id="version-note-${p.slug}" placeholder="Change note (optional)" class="version-note-input" />
            <button class="btn-primary btn-sm" id="version-upload-btn-${p.slug}">Upload</button>
            <span id="version-status-${p.slug}"></span>
          </div>
          <div class="token-section" id="token-section-${p.slug}" style="display:none">
            <button class="btn-secondary btn-sm" id="gen-token-${p.slug}">Generate Upload Token</button>
            <div id="token-display-${p.slug}"></div>
          </div>
        ` : ""}
      </div>
    </div>
  `;
}

async function onKeep(slug: string) {
  try {
    await api.keep(slug);
    await refreshList();
  } catch (e) {
    alert((e as Error).message);
  }
}

async function onUnkeep(slug: string) {
  try {
    await api.unkeep(slug);
    await refreshList();
  } catch (e) {
    alert((e as Error).message);
  }
}

async function onDelete(slug: string) {
  if (!confirm("Delete this prototype? The URL will stop working.")) return;
  try {
    await api.del(slug);
    await refreshList();
  } catch (e) {
    alert((e as Error).message);
  }
}

async function onToggleVersions(p: Prototype) {
  const listEl = document.getElementById(`versions-${p.slug}`)!;
  const toggleBtn = document.getElementById(`versions-toggle-${p.slug}`)!;
  const uploadEl = document.getElementById(`version-upload-${p.slug}`);
  const tokenEl = document.getElementById(`token-section-${p.slug}`);

  if (listEl.style.display === "none") {
    toggleBtn.innerHTML = `&#9660; ${p.versionCount} version${p.versionCount === 1 ? "" : "s"}`;
    listEl.style.display = "";
    if (uploadEl) uploadEl.style.display = "";
    if (tokenEl) tokenEl.style.display = "";
    try {
      const { versions } = await api.listVersions(p.slug);
      listEl.innerHTML = versions.map((v) => renderVersionRow(p, v)).join("");
    } catch (e) {
      listEl.innerHTML = `<div class="error">${escape((e as Error).message)}</div>`;
    }
  } else {
    toggleBtn.innerHTML = `&#9654; ${p.versionCount} version${p.versionCount === 1 ? "" : "s"}`;
    listEl.style.display = "none";
    if (uploadEl) uploadEl.style.display = "none";
    if (tokenEl) tokenEl.style.display = "none";
  }
}

function renderVersionRow(p: Prototype, v: VersionInfo): string {
  const isCurrent = v.version === p.currentVersion;
  const viewUrl = `${VIEWER_BASE}/${p.slug}/v${v.version}/`;
  const viewLink = v.filesRetained
    ? `<a href="${viewUrl}" target="_blank">View</a>`
    : `<span class="version-pruned">Files removed</span>`;
  const badge = isCurrent ? ` <span class="version-current-badge">Current</span>` : "";
  const note = v.note ? `<span class="version-note">${escape(v.note)}</span>` : "";
  const date = new Date(v.uploadedAt);
  const relative = relativeTime(date);

  return `
    <div class="version-row${isCurrent ? " version-current" : ""}">
      <span class="version-num">v${v.version}${badge}</span>
      <span class="version-meta">${escape(v.uploadedBy)} &middot; ${relative}</span>
      ${note}
      <span class="version-link">${viewLink}</span>
    </div>
  `;
}

function relativeTime(date: Date): string {
  const diffMs = Date.now() - date.getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

async function onUploadVersion(slug: string) {
  const file = (document.getElementById(`version-file-${slug}`) as HTMLInputElement).files?.[0];
  const note = (document.getElementById(`version-note-${slug}`) as HTMLInputElement).value.trim();
  const status = document.getElementById(`version-status-${slug}`)!;

  if (!file) { status.textContent = "Pick a .zip file"; return; }

  status.textContent = "Creating version…";
  try {
    const presigned = await api.createVersion(slug, note || undefined);
    status.textContent = "Uploading…";
    const form = new FormData();
    for (const [k, v] of Object.entries(presigned.fields)) form.append(k, v);
    form.append("file", file);
    const res = await fetch(presigned.uploadUrl, { method: "POST", body: form });
    if (!res.ok) throw new Error(`S3 upload failed: ${res.status}`);
    status.textContent = "Extracting…";
    const final = await pollUntilReady(slug, presigned.version);
    if (final.status === "ready") {
      status.innerHTML = `<span class="success">v${presigned.version} ready</span>`;
    } else {
      status.innerHTML = `<span class="error">${escape(final.error ?? "Failed")}</span>`;
    }
    (document.getElementById(`version-file-${slug}`) as HTMLInputElement).value = "";
    (document.getElementById(`version-note-${slug}`) as HTMLInputElement).value = "";
    document.getElementById(`version-filename-${slug}`)!.textContent = "No file chosen";
    await refreshList();
  } catch (e) {
    status.innerHTML = `<span class="error">${escape((e as Error).message)}</span>`;
  }
}

async function onGenerateToken(slug: string) {
  const display = document.getElementById(`token-display-${slug}`)!;
  try {
    const { token, expiresAt } = await api.generateToken(slug);
    const expiresIn = Math.floor((expiresAt - Date.now() / 1000) / 3600);
    const apiEndpoint = getApiUrl();

    display.innerHTML = `
      <div class="token-info">
        <div class="token-value">
          <code id="token-code-${slug}">${escape(token)}</code>
          <button class="copy-btn" id="copy-token-${slug}" title="Copy token">&#128203;</button>
        </div>
        <div class="token-expiry">Expires in ~${expiresIn}h</div>
        <details class="token-usage">
          <summary>Usage instructions</summary>
          <pre>
# 1. Get presigned upload URL
curl -X POST \\
  -H "Authorization: ProtoToken ${escape(token)}" \\
  -H "Content-Type: application/json" \\
  -d '{"note": "your change note"}' \\
  ${escape(apiEndpoint)}/prototypes/${escape(slug)}/versions

# 2. Upload ZIP to the returned URL
curl -X POST &lt;uploadUrl&gt; -F "key=..." -F "file=@prototype.zip"
          </pre>
        </details>
        <button class="btn-secondary btn-sm" id="regen-token-${slug}">Regenerate</button>
      </div>
    `;
    document.getElementById(`copy-token-${slug}`)?.addEventListener("click", () => {
      navigator.clipboard.writeText(token);
      document.getElementById(`copy-token-${slug}`)!.textContent = "\u2713";
      setTimeout(() => { document.getElementById(`copy-token-${slug}`)!.textContent = "\uD83D\uDCCB"; }, 1500);
    });
    document.getElementById(`regen-token-${slug}`)?.addEventListener("click", () => onGenerateToken(slug));
  } catch (e) {
    display.innerHTML = `<span class="error">${escape((e as Error).message)}</span>`;
  }
}

function escape(s: string): string {
  return s.replace(/[&<>"']/g, (c) => (
    c === "&" ? "&amp;" :
    c === "<" ? "&lt;" :
    c === ">" ? "&gt;" :
    c === '"' ? "&quot;" : "&#39;"
  ));
}
